import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, ChevronRight, RefreshCw, Truck, Factory, Users, TrendingUp, Wallet, ClipboardList } from "lucide-react";
import { getWithRetry, describeLoadError } from "./resilient_get";
import { useAutoRefresh } from "./use_auto_refresh";
import { OwnerWatchCards } from "./owner_watch_cards";
import "./owner_control_center.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/owner/control-center`;
const rp = (n) => "Rp" + Math.round(Number(n) || 0).toLocaleString("id-ID");
const fmt = (iso) => iso ? new Date(iso).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) + " WIB" : "belum pernah";
const CH = [["all", "Semua"], ["shopee", "Shopee"], ["tokopedia", "Tokopedia"], ["offline", "Offline"]];
const CH_LABEL = { offline: "Offline", shopee: "Shopee", tokopedia: "Tokopedia" };
// Target halaman pemantauan per kanal; Tokopedia = Pantau Pesanan yang sama dengan kanal Tokopedia aktif.
const MON = { offline: "offline-monitor", shopee: "shopee-monitor", tokopedia: "shopee-monitor" };
const chf = (k, f = {}) => (k === "tokopedia" ? { ...f, channel: "tokopedia" } : f);
const on = (channel, k) => channel === "all" || channel === k;

function Num({ v, label, tone, onClick, testid }) {
  return <button type="button" className={`occ-num occ-${v > 0 ? tone : "ok"}`} onClick={onClick} data-testid={testid}>
    <strong>{v}</strong><span>{label}</span>
  </button>;
}

function TodayShopee({ d, src, go, shp }) {
  return <section className="occ-card" data-testid="occ-shopee-risk">
    <div className="occ-card-head"><Truck size={16}/> <b>Risiko Kirim Shopee</b><small>Batas kirim resmi · tanggal WIB</small></div>
    {!shp ? NA("risk") : <>
    <div className="occ-nums">
      <Num v={d.overdue} label="Lewat batas kirim" tone="red" testid="occ-shp-overdue" onClick={() => go("shopee-monitor", { status: "overdue" })}/>
      <Num v={d.due_today} label="Wajib kirim hari ini" tone="gold" testid="occ-shp-today" onClick={() => go("shopee-monitor", { status: "today" })}/>
    </div>
    <div className="occ-sub">Dari {d.overdue + d.due_today} pesanan itu: belum siap <b>{d.not_ready}</b> · siap <b>{d.ready}</b> · belum masuk produksi <b>{d.not_in_production}</b></div>
    <div className="occ-src">Sinkron pesanan Shopee terakhir: {fmt(src.shopee_orders_sync)}</div>
    </>}
  </section>;
}

function TodayOffline({ d, go }) {
  return <section className="occ-card" data-testid="occ-offline-late">
    <div className="occ-card-head"><AlertTriangle size={16}/> <b>Keterlambatan Offline</b><small>Janji selesai di nota</small></div>
    <div className="occ-nums">
      <Num v={d.overdue} label="Lewat janji selesai" tone="red" testid="occ-off-overdue" onClick={() => go("offline-monitor", { status: "overdue" })}/>
      <Num v={d.due_today} label="Harus selesai hari ini" tone="gold" testid="occ-off-today" onClick={() => go("offline-monitor", { status: "today" })}/>
    </div>
    <div className="occ-sub">Hampir deadline: <b>{d.near}</b></div>
  </section>;
}

function TodayTokopedia({ d, go }) {
  return <section className="occ-card" data-testid="occ-tokopedia-late">
    <div className="occ-card-head"><AlertTriangle size={16}/> <b>Keterlambatan Tokopedia</b><small>Batas kirim yang dicatat manual</small></div>
    <div className="occ-nums">
      <Num v={d.overdue} label="Lewat batas kirim" tone="red" testid="occ-tkp-overdue" onClick={() => go("shopee-monitor", chf("tokopedia", { status: "overdue" }))}/>
      <Num v={d.due_today} label="Wajib kirim hari ini" tone="gold" testid="occ-tkp-today" onClick={() => go("shopee-monitor", chf("tokopedia", { status: "today" }))}/>
    </div>
    <div className="occ-sub">Hampir batas kirim: <b>{d.near}</b></div>
  </section>;
}

function DivRow({ r, k, go, tab }) {
  const waits = Object.entries(r.waiting_reasons || {});
  return <button type="button" className="occ-row" data-testid={`occ-div-${k}-${r.division}`}
    onClick={() => r.division === "Mockup" && k !== "offline" ? go("mockup-monitor", chf(k)) : go(tab, chf(k, { division: r.division }))}>
    <span className="occ-row-name">{r.division}</span>
    <span className="occ-row-stats">
      {r.stalled > 0 && <em className="occ-t-red">{r.stalled} tertahan</em>}
      {r.priority > 0 && <em className="occ-t-gold">{r.priority} dekat/lewat deadline</em>}
      {r.needs_pic > 0 && <em>{r.needs_pic} perlu PIC</em>}
      {!r.issue && <em className="occ-t-green">Lancar</em>}
      {waits.map(([why, n]) => <em key={why} className="occ-muted">{n} {why} (normal)</em>)}
      {r.no_data > 0 && <em className="occ-muted">{r.no_data} data belum cukup</em>}
      <em className="occ-muted">dari {r.active} aktif</em>
    </span>
    <ChevronRight size={14}/>
  </button>;
}

function Bottlenecks({ data, channel, idle, go }) {
  const [showAll, setShowAll] = useState(false);
  const lists = ["offline", "shopee", "tokopedia"].filter(k => on(channel, k)).map(k => [k, CH_LABEL[k], MON[k]]);
  const pics = (data.pics || []).filter(p => channel === "all" || p.channel === channel).slice(0, 8);
  let hidden = 0;
  return <section className="occ-card" data-testid="occ-bottlenecks">
    <div className="occ-card-head"><Factory size={16}/> <b>Hambatan Produksi</b><small>Tertahan = tanpa aktivitas nyata ≥ {idle} jam (Minggu tidak dihitung). Menunggu pembayaran/pengajuan batal = normal, bukan hambatan divisi. Satu pekerjaan bisa masuk lebih dari satu kategori, jadi angka tidak untuk dijumlahkan.</small></div>
    {lists.map(([k, label, tab]) => {
      const rows = (data.bottlenecks?.[k] || []).filter(r => r.active > 0).sort((a, b) => (b.stalled + b.priority) - (a.stalled + a.priority));
      const issues = rows.filter(r => r.issue), rest = rows.filter(r => !r.issue);
      hidden += rest.length;
      return <div key={k} className="occ-div-group">
        {channel === "all" && <div className="occ-ch">{label}</div>}
        {issues.length === 0 && <div className="occ-muted occ-small" data-testid={`occ-div-${k}-clear`}>Tidak ada divisi tertahan atau mendesak.</div>}
        {issues.map(r => <DivRow key={r.division} r={r} k={k} go={go} tab={tab}/>)}
        {showAll && rest.map(r => <DivRow key={r.division} r={r} k={k} go={go} tab={tab}/>)}
      </div>;
    })}
    {hidden > 0 && <button type="button" className="occ-link occ-showall" onClick={() => setShowAll(v => !v)} data-testid="occ-div-show-all">
      {showAll ? "Sembunyikan divisi lancar" : `Lihat Semua Divisi (+${hidden} lancar)`}</button>}
    {pics.length > 0 && <>
      <div className="occ-ch"><Users size={13}/> Penanggung jawab pekerjaan tertahan / mendesak</div>
      <div className="occ-pics">{pics.map(p => <button type="button" key={p.channel + p.name} className={`occ-pic${p.assigned ? "" : " is-unassigned"}`} data-testid={`occ-pic-${p.channel}-${p.name}`}
        onClick={() => {
          if (!p.assigned) return go(MON[p.channel], chf(p.channel, { division: p.divisions[0] }));
          if (p.channel !== "offline" && p.divisions.length === 1 && p.divisions[0] === "Mockup") return go("mockup-monitor", chf(p.channel, { pic: p.name }));
          go(MON[p.channel], chf(p.channel, { pic: p.name, ...(p.divisions.length === 1 ? { division: p.divisions[0] } : {}) }));
        }}>
        <b>{p.name}</b> {p.count} · {p.divisions.join(", ")}{channel === "all" ? ` · ${CH_LABEL[p.channel]}` : ""}
      </button>)}</div>
    </>}
  </section>;
}

// Hanya "in_cancel" yang berasal dari API Shopee; lainnya data produksi internal TAMFARIS.
const API_SRC = new Set(["in_cancel"]);

function Followups({ items, channel, go, shp }) {
  const rows = items.filter(f => on(channel, f.channel) || f.channel === "all");
  return <section className="occ-card" data-testid="occ-followups">
    <div className="occ-card-head"><ClipboardList size={16}/> <b>Perlu Tindak Lanjut</b><small>Beserta operator yang dapat menindaklanjuti</small></div>
    {rows.length === 0 && <div className="occ-muted">Tidak ada yang perlu ditindaklanjuti.</div>}
    {rows.map(f => {
      const api = API_SRC.has(f.key);
      return <button type="button" key={f.key} className="occ-fu" onClick={() => go(f.target.tab, f.target.filter || {})} data-testid={`occ-fu-${f.key}`}>
        <span className="occ-fu-body">
          <span className="occ-fu-title"><b className="occ-fu-count" data-testid={`occ-fu-count-${f.key}`}>{f.count}</b><span className="occ-fu-label">{f.label}</span></span>
          <span className="occ-fu-who"><Users size={12}/><span>{f.who}</span></span>
          {f.note && <span className="occ-fu-note">{f.note}</span>}
          <span className={`occ-fu-src${api ? " is-api" : ""}`} data-testid={`occ-fu-src-${f.key}`}>
            {api ? (shp ? "Data pesanan API Shopee" : "Data pesanan API Shopee belum tersedia di Preview") : "Data internal TAMFARIS"}</span>
        </span>
        <ChevronRight size={18} className="occ-fu-arrow"/>
      </button>;
    })}
  </section>;
}

const perDay = (n, days) => (n / days);
const fmtPD = (v) => v >= 10 ? v.toFixed(0) : v.toFixed(1).replace(".", ",");
// kind: "activity" (pesanan masuk), "output" (penyelesaian), "problem" (terlambat/batal/retur → turun = membaik)
const MEAN = {
  activity: { up: ["▲ Naik", "aktivitas pesanan naik", "g"], down: ["▼ Turun", "aktivitas pesanan turun", "y"] },
  output: { up: ["▲ Naik", "kinerja membaik", "g"], down: ["▼ Turun", "kinerja menurun", "y"] },
  problem: { up: ["▲ Naik", "perlu perhatian", "y"], down: ["▼ Turun", "membaik", "g"] },
};
const trend = (d7, d30, kind) => {
  const a = perDay(d7, 7), b = perDay(d30, 30);
  if (!d30 && !d7) return <span className="occ-muted">—</span>;
  if (!d7 && kind !== "problem") return <span className="occ-muted">Belum ada data 7 hari · periksa kelengkapan data</span>;
  if (Math.abs(a - b) < 0.05 || (b && Math.abs(a - b) / b < 0.1)) return <span className="occ-muted">Stabil</span>;
  const [arrow, text, tone] = MEAN[kind][a > b ? "up" : "down"];
  return <span className={tone === "g" ? "occ-t-green" : "occ-t-gold"}>{arrow} · {text}</span>;
};

function HealthTable({ title, rows }) {
  return <div className="occ-health" data-testid={`occ-health-${title}`}>
    <div className="occ-ch">{title}</div>
    {rows.map(([label, a, b, kind]) => <div key={label} className="occ-hrow" data-testid={`occ-health-${title}-${label}`}>
      <div className="occ-hlabel">{label}</div>
      <div className="occ-hcell"><small>7 hari</small><b>{a}</b><small>{fmtPD(perDay(a, 7))}/hari</small></div>
      <div className="occ-hcell"><small>30 hari</small><b>{b}</b><small>{fmtPD(perDay(b, 30))}/hari</small></div>
      <div className="occ-htrend">{trend(a, b, kind)}</div>
    </div>)}
  </div>;
}

const IS_PREVIEW = /preview/i.test(window.location.hostname);

const NA = (k) => <div className="occ-na" data-testid={`occ-shopee-na-${k}`}>Data pesanan dari API Shopee belum tersedia di Preview. Angka pekerjaan produksi internal TAMFARIS tetap ditampilkan.</div>;

function Health({ h, channel, shp }) {
  const o7 = h.offline.d7, o30 = h.offline.d30, s7 = h.shopee.d7, s30 = h.shopee.d30, t7 = h.tokopedia?.d7, t30 = h.tokopedia?.d30;
  return <section className="occ-card" data-testid="occ-health">
    <div className="occ-card-head"><TrendingUp size={16}/> <b>Perkembangan Usaha</b><small>Dibandingkan per hari: rata-rata 7 hari terakhir vs rata-rata 30 hari terakhir</small></div>
    <div className="occ-src" data-testid="occ-health-note">{IS_PREVIEW
      ? "Preview: tren hanya dihitung dari data yang ada di environment Preview, belum tentu mewakili kondisi LIVE."
      : "Tren dihitung dari data yang tercatat di sistem."}</div>
    {on(channel, "offline") && <HealthTable title="Offline" rows={[["Nota masuk", o7.incoming, o30.incoming, "activity"], ["Diambil/dikirim", o7.handed, o30.handed, "output"],
      ["Terlambat (deadline lewat)", o7.late, o30.late, "problem"]]}/>}
    {on(channel, "shopee") && !shp && <><div className="occ-ch">Shopee</div>{NA("health")}</>}
    {on(channel, "shopee") && shp && <HealthTable title="Shopee" rows={[["Pesanan masuk", s7.incoming, s30.incoming, "activity"], ["Dikirim (pickup)", s7.shipped, s30.shipped, "output"],
      ["Terlambat kirim", s7.late, s30.late, "problem"], ["Batal", s7.cancelled, s30.cancelled, "problem"], ["Pengembalian disetujui", s7.returns, s30.returns, "problem"],
      ["Mockup FIX", s7.mockup_fix, s30.mockup_fix, "output"]]}/>}
    {on(channel, "tokopedia") && t7 && <HealthTable title="Tokopedia" rows={[["Pesanan dicatat", t7.incoming, t30.incoming, "activity"],
      ["Mockup FIX", t7.mockup_fix, t30.mockup_fix, "output"]]}/>}
  </section>;
}

function ChannelAudit({ a }) {
  if (!a) return null;
  return <div className={`occ-src${a.unknown ? " occ-t-gold" : ""}`} data-testid="occ-channel-audit">
    Pekerjaan online aktif: Shopee <b>{a.shopee}</b> · Tokopedia <b>{a.tokopedia}</b>
    {a.unknown > 0 ? <> · <b data-testid="occ-channel-unknown">{a.unknown}</b> kanal belum jelas (tidak dihitung ke Shopee maupun Tokopedia): {a.unknown_orders.join(", ")}</> : " · semua kanal terverifikasi"}
  </div>;
}

function Finance({ data, channel, onReceivable, shp }) {
  const o = data.health.offline, s = data.health.shopee;
  return <section className="occ-card" data-testid="occ-finance">
    <div className="occ-card-head"><Wallet size={16}/> <b>Ringkasan Keuangan</b><small>Kanal tidak dijumlahkan · bukan laba</small></div>
    <div className="occ-fin">
      {on(channel, "offline") && <div className="occ-fin-col">
        <div className="occ-ch">Offline · omzet nota (tanggal nota)</div>
        <div>7 hari <b>{rp(o.d7.omzet)}</b></div><div>30 hari <b>{rp(o.d30.omzet)}</b></div>
        <button type="button" className="occ-link" onClick={onReceivable} data-testid="occ-fin-receivable">Piutang saat ini <b>{rp(data.finance.offline_receivable)}</b> <ChevronRight size={12}/></button>
      </div>}
      {on(channel, "shopee") && <div className="occ-fin-col">
        <div className="occ-ch">Shopee · nilai pesanan tercatat</div>
        {!shp ? NA("finance") : <>
        <div>7 hari <b>{rp(s.d7.order_value)}</b></div><div>30 hari <b>{rp(s.d30.order_value)}</b></div>
        <div className="occ-muted occ-small">Belum final: belum dikurangi retur, potongan, dan biaya Shopee. Dana cair / belum cair: data belum tersedia.</div>
        </>}
      </div>}
    </div>
  </section>;
}

export function OwnerControlCenter({ go, onReceivable }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [channel, setChannel] = useState(() => sessionStorage.getItem("occ-channel") || "all");
  // Rincian lama (Kondisi Hari Ini, Hambatan, Tindak Lanjut, Perkembangan, Keuangan) tertutup default — isinya juga ada di detail kartu.
  const [full, setFull] = useState(() => sessionStorage.getItem("occ-full") === "1");
  useEffect(() => { sessionStorage.setItem("occ-full", full ? "1" : "0"); }, [full]);
  useEffect(() => { sessionStorage.setItem("occ-channel", channel); }, [channel]);
  const load = useCallback(async (fresh = false) => {
    try { setData((await getWithRetry(API, { params: fresh ? { fresh: 1 } : {} })).data); setErr(null); }
    catch (e) { setErr(describeLoadError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const { isRefreshing } = useAutoRefresh({ enabled: true, intervalMs: 60000, onRefresh: () => load() });
  return <div className="occ" data-testid="owner-control-center">
    <div className="occ-top">
      <div className="occ-seg" role="tablist">{CH.map(([k, l]) => <button key={k} type="button" role="tab" aria-selected={channel === k}
        className={channel === k ? "is-on" : ""} onClick={() => setChannel(k)} data-testid={`occ-channel-${k}`}>{l}</button>)}</div>
      <button type="button" className="occ-refresh" onClick={() => load(true)} data-testid="occ-refresh" aria-label="Perbarui">
        <RefreshCw size={14} className={isRefreshing ? "spin" : ""}/>
      </button>
    </div>
    {err && <div className="occ-err" data-testid="occ-error">{data ? "Data belum terbaru. " : ""}{err.text}
      <button type="button" onClick={() => load(true)} data-testid="occ-error-retry">Coba Lagi</button></div>}
    {!data && !err && <div className="occ-muted" data-testid="occ-loading">Memuat ringkasan…</div>}
    {data && <>
      <div className="occ-stamp" data-testid="occ-updated">Pusat Kendali diperbarui {fmt(data.generated_at)} · otomatis tiap 1 menit</div>
      <OwnerWatchCards data={data} channel={channel} go={go} onReceivable={onReceivable}/>
      {channel !== "offline" && <ChannelAudit a={data.channel_audit}/>}
      <button type="button" className="occ-link occ-full-toggle" aria-expanded={full} onClick={() => setFull(v => !v)} data-testid="occ-full-toggle">
        {full ? "Sembunyikan rincian lengkap" : "Lihat rincian lengkap (kondisi hari ini, hambatan, tindak lanjut, perkembangan, keuangan)"} <ChevronRight size={12}/></button>
      {full && <>
      <h3 className="occ-h">Kondisi Hari Ini</h3>
      <div className="occ-grid">
        {on(channel, "shopee") && <TodayShopee d={data.shopee_risk} src={data.sources} go={go} shp={data.shopee_available}/>}
        {on(channel, "tokopedia") && data.tokopedia_late && <TodayTokopedia d={data.tokopedia_late} go={go}/>}
        {on(channel, "offline") && <TodayOffline d={data.offline_late} go={go}/>}
      </div>
      <Bottlenecks data={data} channel={channel} idle={data.idle_hours} go={go}/>
      <Followups items={data.followups} channel={channel} go={go} shp={data.shopee_available}/>
      <Health h={data.health} channel={channel} shp={data.shopee_available}/>
      {channel !== "tokopedia" && <Finance data={data} channel={channel} onReceivable={onReceivable} shp={data.shopee_available}/>}
      </>}
      {data.test_orders_excluded > 0 && <div className="occ-muted occ-small" data-testid="occ-test-excluded">{data.test_orders_excluded} nota uji tidak dihitung (data tetap tersimpan di menu Pesanan).</div>}
    </>}
  </div>;
}
